import { create } from "zustand";
import { api } from "@/lib/api";
import type {
  ReadChatDto,
  ReadChatFileDto,
  ReadChatWithFilesDto
} from "@package/types";
import type { AsyncState } from "./types";

interface ChatFiles extends AsyncState {
  files: ReadChatFileDto[];
}

interface ChatsState extends AsyncState {
  chats: ReadChatDto[];
  sendingChatIds: Set<string>;
  filesByChat: Record<string, ChatFiles>;
}

interface ChatsActions {
  loadChats: () => Promise<void>;
  addChat: (chat: ReadChatDto) => void;
  updateChat: (chat: ReadChatDto) => void;
  removeChat: (id: string) => void;
  removeProjectFromChats: (projectId: string) => void;
  setSending: (chatId: string, isSending: boolean) => void;
  loadFiles: (chatId: string) => Promise<void>;
  setFiles: (chatId: string, files: ReadChatFileDto[]) => void;
  reset: () => void;
}

type ChatsStore = ChatsState & ChatsActions;

export const useChatsStore = create<ChatsStore>()((set, get) => ({
  chats: [],
  isLoading: false,
  isLoaded: false,
  error: null,
  sendingChatIds: new Set<string>(),
  filesByChat: {},

  loadChats: async () => {
    if (get().isLoaded || get().isLoading) return;

    set({ isLoading: true, error: null });
    try {
      const data = await api.get<ReadChatDto[]>("/chats");
      set({ chats: data, isLoading: false, isLoaded: true });
    } catch (error) {
      set({ isLoading: false, error: (error as Error).message });
    }
  },

  addChat: (chat: ReadChatDto) =>
    set((state) => ({
      chats: state.chats.some((c) => c.id === chat.id)
        ? state.chats.map((c) => (c.id === chat.id ? chat : c))
        : [chat, ...state.chats]
    })),

  updateChat: (chat: ReadChatDto) =>
    set((state) => ({
      chats: state.chats.map((c) => (c.id === chat.id ? chat : c))
    })),

  removeChat: (id: string) =>
    set((state) => ({
      chats: state.chats.filter((c) => c.id !== id)
    })),

  removeProjectFromChats: (projectId: string) =>
    set((state) => ({
      chats: state.chats.map((chat) =>
        chat.projectId === projectId ? { ...chat, projectId: null } : chat
      )
    })),

  setSending: (chatId: string, isSending: boolean) =>
    set((state) => {
      const newSet = new Set(state.sendingChatIds);
      if (isSending) {
        newSet.add(chatId);
      } else {
        newSet.delete(chatId);
      }
      return { sendingChatIds: newSet };
    }),

  loadFiles: async (chatId: string) => {
    const existing = get().filesByChat[chatId];

    if (existing?.isLoaded || existing?.isLoading) return;

    set((state) => ({
      filesByChat: {
        ...state.filesByChat,
        [chatId]: { files: [], isLoading: true, isLoaded: false, error: null }
      }
    }));

    try {
      const chat = await api.get<ReadChatWithFilesDto>(`/chats/${chatId}`);
      set((state) => ({
        filesByChat: {
          ...state.filesByChat,
          [chatId]: {
            files: chat.files || [],
            isLoading: false,
            isLoaded: true,
            error: null
          }
        }
      }));
    } catch (error) {
      set((state) => ({
        filesByChat: {
          ...state.filesByChat,
          [chatId]: {
            files: state.filesByChat[chatId]?.files ?? [],
            isLoaded: state.filesByChat[chatId]?.isLoaded ?? false,
            isLoading: false,
            error: (error as Error).message
          }
        }
      }));
    }
  },

  setFiles: (chatId: string, files: ReadChatFileDto[]) =>
    set((state) => ({
      filesByChat: {
        ...state.filesByChat,
        [chatId]: { files, isLoading: false, isLoaded: true, error: null }
      }
    })),

  reset: () =>
    set({
      chats: [],
      isLoading: false,
      isLoaded: false,
      error: null,
      sendingChatIds: new Set<string>(),
      filesByChat: {}
    })
}));
